import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Spinner from './Spinner';
import FormattedMessageWrappedInSpan from './FormattedMessageWrappedInSpan';

import { Tooltip } from '@material-ui/core';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import { Copy } from '@carbon/icons-react';
import { HelpFilled } from '@carbon/icons-react';

////////////////////////////////////////////
// TODO MISSING TESTS
////////////////////////////////////////////

const DefaultButton = (props) => {
  const {
    className,
    disabled,
    hasFullWidth,
    hasSpinner,
    leftLabel,
    onClick,
    rightLabel,
    spinnerColor,
    isInSubsectionFrame,
    isLabelOrderReversed,
    isRightLabelPosAbsolute
  } = props;

  const _renderLabel = (label) => {
    if (React.isValidElement(label)) {
      return label;
    }
    return <span>{label}</span>;
  };

  return (
    <button
      className={classNames(className, {
        'full-width': hasFullWidth,
        disabled: disabled,
        'btn-default': !isInSubsectionFrame,
        'btn': !isInSubsectionFrame,
      })}
      disabled={disabled}
      onClick={onClick ? onClick : null}
    >
      <div
        className={classNames('btn-default-content', {
          'btn-reversed-content': isLabelOrderReversed,
        })}
      >
        <div className="left-label">{_renderLabel(leftLabel)}</div>
        {rightLabel && (
          <div
            className={classNames('btn-right-label-container', {
              'label-inline': !isRightLabelPosAbsolute,
            })}
          >
            {_renderLabel(rightLabel)}
          </div>
        )}
        {hasSpinner && (
          <div className="btn-right-label-container mr-20 button-circular-progress-container">
            <Spinner color={spinnerColor} size="20px" />
          </div>
        )}
      </div>
    </button>
  );
};

DefaultButton.defaultProps = {
  hasSpinner: false,
  hasFullWidth: false,
  disabled: false,
  isInSubsectionFrame: false,
  isLeftLabelPosAbsolute: true,
  isLabelOrderReversed: false,
};

DefaultButton.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  hasSpinner: PropTypes.bool,
  hasFullWidth: PropTypes.bool,
  leftLabel: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  rightLabel: PropTypes.node,
  spinnerColor: PropTypes.string,
  isInSubsectionFrame: PropTypes.bool,
  isLeftLabelPosAbsolute: PropTypes.bool,
  isLabelOrderReversed: PropTypes.bool,
};

/****************************************************************************************

                                        Primary Button

*****************************************************************************************/

export const PrimaryButton = (props) => {
  return (
    <DefaultButton {...props} className={classNames('primary-new', props.className)} isInSubsectionFrame={props.isInSubsectionFrame} />
  );
};

PrimaryButton.propTypes = {
  className: PropTypes.string,
  isInSubsectionFrame: PropTypes.bool,
};

/****************************************************************************************

                                        Secondary Button

*****************************************************************************************/

export const SecondaryButton = (props) => {
  return (
    <DefaultButton {...props} className={classNames('secondary-new', props.className)} />
  );
};

SecondaryButton.propTypes = {
  className: PropTypes.string,
};

/****************************************************************************************

                                          Text Button

*****************************************************************************************/

export const TextButton = (props) => {
  return (
    <DefaultButton
      {...props}
      className={classNames('primary-text-new', props.className)}
    />
  );
};

TextButton.propTypes = {
  className: PropTypes.string,
};

/****************************************************************************************

                                    Icon button

*****************************************************************************************/

export const IconRoundedButton = (props) => {
  return (
    <button
      {...props}
      className={classNames(
        'rounded-icon-button',
        props.className,
        props.size,
        props.color,
        {
          disabled: props.disabled,
          'no-hover-effects': props.noHoverEffects,
        }
      )}
      disabled={props.disabled}
      onClick={props.onClick ? props.onClick : null}
    >
      {props.icon}
    </button>
  );
};

IconRoundedButton.defaultProps = {
  size: 'medium',
  color: 'blue',
  noHoverEffects: false,
};

IconRoundedButton.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.object,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium']),
  color: PropTypes.oneOf(['blue', 'black', 'grey', 'red']),
  noHoverEffects: PropTypes.bool,
};


/****************************************************************************************

                                    Animated icon to copy sth to clipboard

*****************************************************************************************/

export const CopyToClipboardIcon = (props) => {
  const { stringToCopy, color } = props;

  const [stringCopied, setStringCopied] = useState(false);
  const [error, setError] = useState(false);

  const copyStringToClipboard = () => {
    navigator.clipboard.writeText(stringToCopy).then(
      () => {
        setStringCopied(true);
        setTimeout(() => {
          setStringCopied(false);
        }, '1000');
      },
      (error) => {
       setError(true)
       setTimeout(() => {
        setError(false);
      }, '1000');
      }
    );
  };

  const renderTooltipText = () => {
    if (stringCopied) {
      return <FormattedMessageWrappedInSpan
      id="copyToClipboardIcon.copiedMessage"
      defaultMessage="Copied"
    />
    } else if (error) {
      return <FormattedMessageWrappedInSpan
      id="copyToClipboardIcon.errorMessage"
      defaultMessage="Error"
    />
    }
    return   <FormattedMessageWrappedInSpan
    id="copyToClipboardIcon.copyMessage"
    defaultMessage="Copy"
  />
  }

  return (
    <Tooltip
      title={renderTooltipText()}
    >
      <span>
        <IconRoundedButton
          {...props}
          color={error ? 'red' : color}
          onClick={() => copyStringToClipboard()}
          className={`with-animation ${stringCopied ? 'circleAnimation' : ''}`}
          icon={stringCopied ? <CheckRoundedIcon /> : <Copy />}
        />
      </span>
    </Tooltip>
  );
};

CopyToClipboardIcon.defaultProps = {
  size: 'small',
  color: 'blue',
};

CopyToClipboardIcon.propTypes = {
  size: PropTypes.oneOf(['small', 'medium']),
  color: PropTypes.oneOf(['blue', 'black', 'grey']),
  stringToCopy: PropTypes.string,
};

/****************************************************************************************

                                        Disabled to active
disabled by default, after 3 seconds is active 

*****************************************************************************************/

export const DisabledToActiveButton = forwardRef((props, ref) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [animationStarted, setAnimationStarted] = useState(false);
  const buttonRef = useRef();

  useImperativeHandle(ref, () => buttonRef.current);

  useEffect(() => {
    setAnimationStarted(true);
    const timer = setTimeout(() => {
      setIsDisabled(false);
      if (buttonRef.current) {
        buttonRef.current.focus();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <button
      {...props}
      ref={buttonRef}
      className={classNames('btn-disabed-to-active', props.className, {
        disabled: isDisabled,
        animate: animationStarted,
      })}
      disabled={isDisabled}
      type="button"
    >
      {props.children}
    </button>
  );
});

DisabledToActiveButton.propTypes = {
  className: PropTypes.string,
  buttonIsRendered: PropTypes.bool,
};


/****************************************************************************************

                                        (?) icon in the corner of the button

*****************************************************************************************/

export const ButtonQuestionMark = () => {
  return <div className='btn-question-icon'><HelpFilled /></div>
}


